'use client'

import { useSignIn } from '@clerk/nextjs'
import {
  type ButtonProps,
  Button,
} from '@snowball-tech/fractal/dist/src/components/Button'
import { useSearchParams } from 'next/navigation'

import omit from 'lodash/fp/omit'

import { handleGoogleSignup } from '../helpers/authentication'
import useFeatureFlags from '../hooks/useFeatureFlags'
import useTracking from '../hooks/useTracking'
import useTranslations from '../hooks/useTranslations'
import GoogleIcon from './GoogleIcon'

type Props = {
  metadata?: Record<string, string | null | undefined>
  returnUrl?: string
} & Omit<ButtonProps, 'icon' | 'onClick'>

export default function GoogleConnectButton({
  metadata = {},
  returnUrl = '/',
  ...props
}: Props) {
  const searchParams = useSearchParams()
  const $t = useTranslations()

  const { track } = useTracking()

  const { allowGoogleAuth } = useFeatureFlags()

  const { isLoaded, signIn: clerkSignin } = useSignIn()

  if (!allowGoogleAuth || !isLoaded) {
    return false
  }

  const handleClick = () => {
    track('auth.google.click', { metadata, returnUrl, searchParams })

    handleGoogleSignup(clerkSignin, returnUrl, searchParams, metadata)
  }

  return (
    <Button
      {...omit(['icon', 'label', 'onClick'], props)}
      icon={<GoogleIcon />}
      iconPosition="left"
      label={props.label || $t('signin.form.google.button.label')}
      onClick={handleClick}
    >
      {props.children}
    </Button>
  )
}
